import { isNil } from 'ramda';
import { IS_AUTO_TEST_HIDE } from 'reactApp/appHelpers/configHelpers';
import {
    onboardingOpeningDelayMS,
    onboardingStepsEnabled,
    onboardingStepsSettings,
} from 'reactApp/appHelpers/featuresHelpers/features/onboardingSteps';
import { getItemFromLocalStorage, setItemFromLocalStorage } from 'reactApp/appHelpers/localstorageHelpers';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { loadFolderSuccess } from 'reactApp/modules/home/home.actions';
import { getFolderListLengthById, getHomeFolderPublicFilesCount } from 'reactApp/modules/home/home.selectors';
import { publishWeblink, uploadFilesEnded } from 'reactApp/modules/modifying/modifying.actions';
import {
    expandOnboardingPanel,
    setCurrentStep,
    setHintForShareStep,
    showHintForCurrentStep,
    showOnboardingPanel,
} from 'reactApp/modules/onboardingSteps/onboardingSteps.module';
import {
    getHomeFolderFirstChild,
    getOnboardingPanelOpen,
    getOnboardingPanelVisible,
    getOnboardingPeriodAvailability,
    getOnboardingStepIndex,
} from 'reactApp/modules/onboardingSteps/onboardingSteps.selector';
import { Step, StepName } from 'reactApp/modules/onboardingSteps/onboardingSteps.types';
import { loadPaidInfoRequest } from 'reactApp/modules/paidInfo/paidInfo.module';
import { handlePaidInfoSuccess } from 'reactApp/modules/paidInfo/paidInfo.saga';
import { PaidInfoSelectors } from 'reactApp/modules/paidInfo/paidInfo.selectors';
import { initProducts, loadProductsSuccess } from 'reactApp/modules/products/products.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { getUserMobilePlatforms } from 'reactApp/modules/profile/profile.selectors';
import { routeChangeSuccess } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { updateTotalProgress } from 'reactApp/modules/uploadList/uploadList.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { sendDwh } from 'reactApp/utils/ga';
import { call, cancel, delay, fork, put, select, take, takeLatest } from 'redux-saga/effects';

import { setProfileOverquotaStateSuccess } from '../profile/profile.module';
import {
    AnyliticsActions,
    AnyliticsCategories,
    COMPLETION_STATE,
    ONBOARDING_PROGRESS_KEY,
    ONBOARDING_STATE_KEY,
} from './onboardingSteps.constants';

function* sendOpenAnalytics(step: Step) {
    const isPaidUser = yield select(UserSelectors.isPaidUser);
    const isPanelOpen = yield select(getOnboardingPanelOpen);

    sendDwh({
        eventCategory: AnyliticsCategories.STEPS,
        action: AnyliticsActions.SHOW,
        dwhData: {
            step,
            step_name: StepName[step],
            expanded: isPanelOpen,
            paid_user: isPaidUser,
        },
    });
}

function* showHintForShare() {
    yield delay(300);
    const step = yield select(getOnboardingStepIndex);

    if (step === Step.SHARE) {
        const rootFirstChild = yield select(getHomeFolderFirstChild);
        yield put(setHintForShareStep({ itemId: rootFirstChild }));
    }
}

function* checkIsCompleted() {
    const onboardingCompletion = yield put(getItemFromLocalStorage(ONBOARDING_STATE_KEY));
    return onboardingCompletion === COMPLETION_STATE;
}

function* getCurrentStep() {
    const rootFolderLength = yield select((state) => getFolderListLengthById(state, ROOT_FOLDER_ID));
    const rootFolderPublicFilesCount = yield select(getHomeFolderPublicFilesCount);
    const userDevicePlatforms = yield select(getUserMobilePlatforms);
    const isAppInstalledOnMobileDevices = userDevicePlatforms.length > 0;

    const hasUploadedFiles = rootFolderLength > 0;
    const hasPublicFiles = rootFolderPublicFilesCount > 0;
    let step: Step | null = null;

    if (!hasUploadedFiles) {
        step = Step.UPLOAD;
    }

    if (hasUploadedFiles && !hasPublicFiles) {
        step = Step.SHARE;
    }

    if (hasUploadedFiles && hasPublicFiles) {
        step = Step.DOWNLOAD_APP;
    }

    if (hasUploadedFiles && hasPublicFiles && isAppInstalledOnMobileDevices) {
        step = Step.ACHIEVEMENT;
    }

    return step;
}

function* updateOnboardingState() {
    const storage = yield select(getCurrentStorage);
    const { isHome } = getStorage(storage);
    // Дополонительно проверяем что онбординг пройден
    // Такой кейс возможен когда взяли триал,
    // а сага уже инициализирована и подписана на экшены
    const isCompleted = yield call(checkIsCompleted);

    if (isHome && !isCompleted) {
        const progress = yield put(getItemFromLocalStorage(ONBOARDING_PROGRESS_KEY));
        const step = yield call(getCurrentStep);
        const isPanelVisible = yield select(getOnboardingPanelVisible);

        if (!isNil(step)) {
            // Выставляем сохраненный прогресс
            yield put(setCurrentStep({ step: progress > step ? progress : step }));

            if (!isPanelVisible) {
                yield put(showOnboardingPanel());
                yield call(sendOpenAnalytics, step);
            }

            if (!isNil(progress) && progress === step) {
                return;
            }

            // Если пользователь прошел дальше по прогрессу, то единожды раскрываем онбоардинг и показываем хинт
            if (isNil(progress) || (step !== progress && progress < step)) {
                yield delay(onboardingOpeningDelayMS);

                const storage = yield select(getCurrentStorage);
                const { isHome } = getStorage(storage);

                // За 10с перед первым распахиванием пользователь может куда-нибудь уйти, поэтому проверяем что мы на хомяке
                if (isHome) {
                    yield put(expandOnboardingPanel());

                    if (step !== Step.SHARE) {
                        yield put(showHintForCurrentStep());
                    } else {
                        yield call(showHintForShare);
                    }

                    yield call(sendOpenAnalytics, step);
                }
            }

            if (isNil(progress) || step > progress) {
                yield put(setItemFromLocalStorage(ONBOARDING_PROGRESS_KEY, step));
            }
        }
    }
}

function* initOnboarding() {
    // Дожидаемся загрузки ручки /full_profile. На АТ пропускаем шаг, так как будет бесконечно ждать результат; оверквоты у нового юзера не будет
    if (!IS_AUTO_TEST_HIDE) {
        yield take(setProfileOverquotaStateSuccess.toString());
    }

    const { isLoaded } = yield select(ProductsSelectors.getLifeCycleState);
    const isOnboardingPeriodAvailabile = yield select(getOnboardingPeriodAvailability);

    if (!isOnboardingPeriodAvailabile) {
        yield put(setItemFromLocalStorage(ONBOARDING_STATE_KEY, COMPLETION_STATE));
        yield cancel();
    }

    if (!isLoaded) {
        yield put(initProducts());
        yield take(loadProductsSuccess.toString());
    }

    const paidInfo = yield select(PaidInfoSelectors.getLoadingState);

    // Если нет платежной инфы - нужно ее подгрузить, иначе не откроется окно fastCheckout
    if (!paidInfo.isLoaded || !paidInfo.isLoading) {
        yield put(loadPaidInfoRequest());
        yield handlePaidInfoSuccess();
    }

    const isPaidUser = yield select(UserSelectors.isPaidUser);
    const isB2BUser = yield select(UserSelectors.isB2BUser);
    const isUserWithoutPayment = yield select(PaidInfoSelectors.isUserWithoutPayment);
    const isTrialAvailable = yield select((state) => ProductsSelectors.isTrialAvailable(state, onboardingStepsSettings?.tariff));
    const isUserCanUseTrial = !isPaidUser && !isB2BUser && isUserWithoutPayment && isTrialAvailable;

    if (!isUserCanUseTrial) {
        yield put(setItemFromLocalStorage(ONBOARDING_STATE_KEY, COMPLETION_STATE));
        yield cancel();
    }

    yield call(updateOnboardingState);
}

function* watchForUserUpdates() {
    yield takeLatest(
        [
            loadFolderSuccess.toString(),
            publishWeblink.toString(),
            uploadFilesEnded.toString(),
            updateTotalProgress.toString(),
            routeChangeSuccess.toString(),
        ],
        updateOnboardingState
    );
}

export function* watchOnboarding() {
    const isCompleted = yield call(checkIsCompleted);

    if (onboardingStepsEnabled && !isCompleted) {
        yield call(initOnboarding);
        yield fork(watchForUserUpdates);
    }
}
